import { puzzelAuthConfig } from "app/PuzzelAuth/puzzelAuthConfig";
import {
  clearPuzzelTokens,
  getPuzzelRefreshToken,
  getValidPuzzelAccessToken,
  registerPuzzelLoginAttempt,
  savePuzzelTokens,
} from "app/PuzzelAuth/puzzelTokenStorage";
import axios from "axios";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import pkceChallenge from "pkce-challenge";

const STATE_KEY = "PUZZEL_AUTH_STATE";
const CODE_VERIFIER_KEY = "PUZZEL_AUTH_CODE_VERIFIER";
const RETURN_PATH_KEY = "PUZZEL_AUTH_RETURN_PATH";

export const PUZZEL_LOGIN_REDIRECT_FAILED_EVENT =
  "puzzel-login-redirect-failed";

const logSource = "puzzelAuth";

type PuzzelTokenResponse = {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
};

const exchangeTokenRequest = (
  body: URLSearchParams,
): Promise<{ data: PuzzelTokenResponse }> =>
  axios.post(puzzelAuthConfig.tokenEndpoint, body, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });

export const redirectToPuzzelLogin = async (): Promise<void> => {
  if (!puzzelAuthConfig.clientId) {
    log(
      LogLevel.error,
      logSource,
      "Puzzel client id is not set (expected from /app-data at boot) - cannot start Puzzel login",
    );
    throw new Error("Missing Puzzel client id");
  }

  if (!registerPuzzelLoginAttempt()) {
    log(
      LogLevel.error,
      logSource,
      "Giving up after repeated Puzzel logins still failed - likely a scope/audience or " +
        "customer key misconfiguration rather than an expired session.",
    );
    throw new Error("Too many Puzzel login attempts");
  }

  const { code_verifier: codeVerifier, code_challenge: codeChallenge } =
    await pkceChallenge();
  const state = crypto.randomUUID();

  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
  sessionStorage.setItem(
    RETURN_PATH_KEY,
    `${window.location.pathname}${window.location.search}`,
  );

  const authorizeParams = new URLSearchParams({
    client_id: puzzelAuthConfig.clientId,
    redirect_uri: puzzelAuthConfig.redirectUri,
    response_type: "code",
    scope: puzzelAuthConfig.scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  if (puzzelAuthConfig.idpScheme) {
    authorizeParams.set("idp", puzzelAuthConfig.idpScheme);
  }

  const authorizeUrl = new URL(puzzelAuthConfig.authorizeEndpoint);
  authorizeUrl.search = authorizeParams.toString();

  window.location.assign(authorizeUrl.toString());
};

// Handles the redirect back from Puzzel ID; returns the path to navigate back to after login.
export const handlePuzzelCallback = async (): Promise<string> => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
  const returnPath = sessionStorage.getItem(RETURN_PATH_KEY) ?? "/";
  // Some IdP-side errors (e.g. access_denied) redirect back to us with these instead of a code -
  // misconfiguration errors (e.g. invalid_scope) more often show Puzzel's own hosted error page
  // and never redirect back at all, so this won't catch every case, but handles what it can.
  const idpError = params.get("error");
  const idpErrorDescription = params.get("error_description");

  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(CODE_VERIFIER_KEY);
  sessionStorage.removeItem(RETURN_PATH_KEY);

  if (idpError) {
    log(
      LogLevel.error,
      logSource,
      `Puzzel login redirected back with an error: ${idpError} - ${idpErrorDescription}`,
    );
    throw new Error(idpErrorDescription ?? idpError);
  }

  if (!code || !state || !codeVerifier || state !== expectedState) {
    log(
      LogLevel.error,
      logSource,
      "Puzzel login callback missing/mismatched code, state or code_verifier",
    );
    throw new Error("Invalid Puzzel login callback");
  }

  const response = await exchangeTokenRequest(
    new URLSearchParams({
      grant_type: "authorization_code",
      client_id: puzzelAuthConfig.clientId,
      redirect_uri: puzzelAuthConfig.redirectUri,
      code,
      code_verifier: codeVerifier,
    }),
  );

  savePuzzelTokens({
    accessToken: response.data.access_token,
    refreshToken: response.data.refresh_token,
    expiresInSeconds: response.data.expires_in,
  });

  return returnPath;
};

const isRefreshTokenRejected = (error: unknown) =>
  axios.isAxiosError(error) &&
  (error.response?.status === 400 || error.response?.status === 401);

// The OAuth error code in the token endpoint's error response, e.g. "invalid_grant".
const getOAuthErrorCode = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }
  const code = (error.response?.data as { error?: unknown } | undefined)?.error;
  return typeof code === "string" ? code : undefined;
};

export const refreshPuzzelToken = async (): Promise<string | null> => {
  const refreshToken = getPuzzelRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await exchangeTokenRequest(
      new URLSearchParams({
        grant_type: "refresh_token",
        client_id: puzzelAuthConfig.clientId,
        refresh_token: refreshToken,
      }),
    );

    savePuzzelTokens({
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token ?? refreshToken,
      expiresInSeconds: response.data.expires_in,
    });

    return response.data.access_token;
  } catch (error) {
    if (isRefreshTokenRejected(error)) {
      const errorCode = getOAuthErrorCode(error);
      // invalid_grant is the normal end of a session, so log it as a warning.
      log(
        errorCode === "invalid_grant" ? LogLevel.warn : LogLevel.error,
        logSource,
        `Puzzel rejected the refresh token (${errorCode ?? "unknown error"})`,
        error,
      );
      clearPuzzelTokens();
      return null;
    }
    log(
      LogLevel.warn,
      logSource,
      "Temporary error refreshing Puzzel token",
      error,
    );
    throw error;
  }
};

// Dedupes concurrent refresh calls so two callers never race on the same (possibly rotating)
// refresh_token - the loser would otherwise wipe out the winner's freshly-saved tokens on failure.
let inFlightRefresh: Promise<string | null> | null = null;

const PUZZEL_REFRESH_LOCK_NAME = "puzzel-refresh-token";

// Serializes refresh attempts *across tabs* too: navigator.locks queues same-named lock requests
// from other tabs/browsing contexts, so two tabs can never both send the same rotating
// refresh_token at once (tokens now live in localStorage and are shared between tabs). Falls
// back to running unprotected if the API isn't available (outside our supported browser matrix).
const withPuzzelRefreshLock = async <T>(
  fn: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> => {
  if (typeof navigator === "undefined" || !navigator.locks) {
    return fn();
  }
  return navigator.locks.request(PUZZEL_REFRESH_LOCK_NAME, { signal }, fn);
};

const dedupedRefreshPuzzelToken = (): Promise<string | null> => {
  if (!inFlightRefresh) {
    inFlightRefresh = withPuzzelRefreshLock(async () => {
      const alreadyValid = getValidPuzzelAccessToken();
      if (alreadyValid) {
        return alreadyValid;
      }
      return refreshPuzzelToken();
    }).finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
};

const revokePuzzelRefreshToken = async (): Promise<void> => {
  const refreshToken = getPuzzelRefreshToken();
  if (!refreshToken) {
    return;
  }

  await axios.post(
    puzzelAuthConfig.revocationEndpoint,
    new URLSearchParams({
      token: refreshToken,
      token_type_hint: "refresh_token",
      client_id: puzzelAuthConfig.clientId,
    }),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      // Logout waits for this, so don't let a slow Puzzel hold it up.
      timeout: 2000,
    },
  );
};

// Revokes the refresh token at Puzzel
// Drops the local copies
export const revokeAndClearPuzzelTokens = async (): Promise<void> => {
  try {
    // Under the refresh lock, so another tab can't rotate the token while it's being revoked
    await withPuzzelRefreshLock(
      revokePuzzelRefreshToken,
      AbortSignal.timeout(3000),
    );
  } catch (error) {
    log(
      LogLevel.warn,
      logSource,
      "Failed to revoke Puzzel refresh token",
      error,
    );
  }
  clearPuzzelTokens();
};

// Dedupes concurrent callers so an expired token only triggers one refresh-or-redirect attempt.
let inFlightAuth: Promise<string> | null = null;

// Ensures a valid token, refreshing or redirecting to Puzzel login as needed (redirect never resolves).
export const ensurePuzzelAuthenticated = (): Promise<string> => {
  const existingToken = getValidPuzzelAccessToken();
  if (existingToken) {
    return Promise.resolve(existingToken);
  }

  if (!inFlightAuth) {
    inFlightAuth = (async () => {
      try {
        const refreshedToken = await dedupedRefreshPuzzelToken();
        if (refreshedToken) {
          return refreshedToken;
        }

        try {
          await redirectToPuzzelLogin();
        } catch (error) {
          window.dispatchEvent(new Event(PUZZEL_LOGIN_REDIRECT_FAILED_EVENT));
          throw error;
        }
        return await new Promise<string>(() => {});
      } finally {
        // Skipped if we actually navigated away; only clears the lock when staying on the page.
        inFlightAuth = null;
      }
    })();
  }

  return inFlightAuth;
};

// Non-redirecting check, so Puzzel login can be opt-in instead of blocking the whole dashboard.
export const checkPuzzelSession = async (): Promise<boolean> => {
  if (getValidPuzzelAccessToken()) {
    return true;
  }
  return (await dedupedRefreshPuzzelToken()) !== null;
};
